import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, MapPin, Plus, X, ChevronRight, CheckCircle, Clock, Users, Megaphone, Star } from "lucide-react";
import { PROPERTIES, CAMPAIGNS, LEADS } from "@/data/dummyData";

const STATUS_STYLES = {
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  construction: "bg-amber-50 text-amber-700 border-amber-200",
  sold_out: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABELS = { ready: "Ready to Move", construction: "Under Construction", sold_out: "Sold Out" };

function PropertyModal({ property, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const propertyCampaigns = CAMPAIGNS.filter(c => c.property === property.name);
  const propertyLeads = LEADS.filter(l => l.propertyInterest === property.name);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8"
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        data-testid="property-modal"
      >
        {/* Hero Image */}
        <div className="relative h-56 rounded-t-2xl overflow-hidden">
          <img src={property.image} alt={property.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-slate-700 hover:bg-white">
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-4 left-5">
            <h2 className="font-heading text-2xl font-bold text-white">{property.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <MapPin className="w-3.5 h-3.5 text-white/80" />
              <p className="text-white/80 text-sm">{property.location}</p>
            </div>
          </div>
          <div className={`absolute top-4 left-5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLES[property.status]}`}>
            {STATUS_LABELS[property.status]}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-5">
          {["overview", "campaigns", "leads", "gallery"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-semibold capitalize transition-colors border-b-2 ${activeTab === tab ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`property-tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === "overview" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{property.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { label: "Type", value: property.type },
                    { label: "Config", value: property.config },
                    { label: "Area", value: property.area },
                    { label: "Starting Price", value: property.price },
                    { label: "Total Units", value: property.totalUnits },
                    { label: "Available", value: property.available },
                  ].map(item => (
                    <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                      <p className="text-xs text-slate-400">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-800">{item.value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400">RERA: {property.rera}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Amenities</p>
                <div className="grid grid-cols-2 gap-2">
                  {property.amenities.map(a => (
                    <div key={a} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      {a}
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    { label: "Total Leads", value: property.totalLeads },
                    { label: "Bookings", value: property.bookings },
                    { label: "Campaigns", value: property.activeCampaigns },
                  ].map(stat => (
                    <div key={stat.label} className="text-center bg-blue-50 rounded-xl p-3">
                      <p className="font-heading text-xl font-bold text-blue-600">{stat.value}</p>
                      <p className="text-xs text-blue-700 mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "campaigns" && (
            <div className="space-y-3">
              {propertyCampaigns.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">No campaigns for this property yet.</p>
              ) : propertyCampaigns.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.leads} leads · CPL ₹{c.cpl} · ROAS {c.roas}x</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${c.status === "active" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "leads" && (
            <div className="space-y-2">
              {propertyLeads.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">No leads for this property yet.</p>
              ) : propertyLeads.map(l => (
                <div key={l.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                    {l.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-800">{l.name}</p>
                    <p className="text-xs text-slate-500">{l.budget} · {l.config}</p>
                  </div>
                  <span className="text-xs font-bold text-blue-600">{l.score}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "gallery" && (
            <div className="grid grid-cols-3 gap-3">
              {property.gallery?.map((img, i) => (
                <img key={i} src={img} alt={`${property.name} ${i + 1}`} className="w-full h-32 object-cover rounded-xl" />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function Properties() {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = PROPERTIES.filter(p => filter === "all" || p.status === filter);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Properties</h2>
          <p className="text-slate-500 text-sm mt-0.5">Elite Estate · {PROPERTIES.length} properties</p>
        </div>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors" data-testid="add-property-btn">
          <Plus className="w-4 h-4" /> Add Property
        </button>
      </div>

      {/* Portfolio Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Properties", value: PROPERTIES.length, icon: Building2, color: "text-blue-600" },
          { label: "Total Leads", value: PROPERTIES.reduce((s, p) => s + p.totalLeads, 0).toLocaleString(), icon: Users, color: "text-indigo-600" },
          { label: "Active Campaigns", value: PROPERTIES.reduce((s, p) => s + p.activeCampaigns, 0), icon: Megaphone, color: "text-purple-600" },
          { label: "Total Bookings", value: PROPERTIES.reduce((s, p) => s + p.bookings, 0), icon: Star, color: "text-emerald-600" },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div>
              <p className="font-heading text-xl font-bold text-slate-900">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[{ id: "all", label: "All" }, { id: "ready", label: "Ready to Move" }, { id: "construction", label: "Under Construction" }].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f.id ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            data-testid={`property-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Properties Grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filtered.map((property, i) => (
          <motion.div
            key={property.id}
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
            onClick={() => setSelected(property)}
            data-testid={`property-card-${property.id}`}
            whileHover={{ y: -2 }}
          >
            {/* Image */}
            <div className="relative h-44 overflow-hidden">
              <img
                src={property.image}
                alt={property.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLES[property.status]}`}>
                {STATUS_LABELS[property.status]}
              </div>
              <div className="absolute bottom-3 left-3">
                <p className="font-heading text-white font-bold text-lg leading-tight">{property.name}</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center gap-1.5 text-slate-500 mb-3">
                <MapPin className="w-3.5 h-3.5" />
                <p className="text-xs">{property.location}</p>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-heading text-xl font-bold text-slate-900">{property.price}</p>
                  <p className="text-xs text-slate-500">{property.type} · {property.config} · {property.area}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800">{property.available}</p>
                  <p className="text-xs text-slate-500">units left</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-100">
                <div className="text-center">
                  <p className="text-sm font-bold text-blue-600">{property.totalLeads}</p>
                  <p className="text-xs text-slate-400">Leads</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-indigo-600">{property.activeCampaigns}</p>
                  <p className="text-xs text-slate-400">Campaigns</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-emerald-600">{property.bookings}</p>
                  <p className="text-xs text-slate-400">Bookings</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selected && <PropertyModal property={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
